import { useCallback, useContext } from 'react';
import { I18nContext } from './provider.js';
import { formatMessage } from './format_message.js';
import type {
  TranslateParams,
  TranslateOptions,
  TFunction,
  UseTranslationResult,
} from './types.js';

/**
 * Hook that returns a `t()` function scoped to the given namespace,
 * the current locale, and a `setLocale` callback.
 *
 * @param namespace - The translation namespace (e.g. `'common'`, `'nav'`).
 *   Keys are looked up as `${namespace}.${key}` in the active bundle.
 */
export function useTranslation(namespace: string): UseTranslationResult {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    throw new Error(
      'useTranslation must be used inside an <I18nProvider>. ' +
        'Wrap your component tree with <I18nProvider> before calling useTranslation().',
    );
  }

  const { bundle, locale, fallbackBundle, setLocale } = ctx;

  // Memoize t so callers that include it in useEffect/useMemo/useCallback dep
  // arrays don't re-fire on every render. t only changes when the locale,
  // bundle, fallbackBundle, or namespace actually change.
  const t = useCallback<TFunction>(
    (key: string, options?: TranslateOptions | TranslateParams): string => {
      const fullKey = `${namespace}.${key}`;

      // Resolution order: active bundle → fallback bundle → literal key
      const message =
        bundle[fullKey] ??
        (fallbackBundle !== undefined
          ? (fallbackBundle[fullKey] ?? fullKey)
          : fullKey);

      // Strip the `description` field — it is for translator tooling only and
      // must not be passed as an ICU interpolation variable.
      let params: TranslateParams | undefined;
      if (options !== undefined) {
        const { description: _description, ...rest } = options;
        params = Object.keys(rest).length > 0 ? rest : undefined;
      }

      // Dev-only: warn when a string resolves to an untranslated stub value
      // (stubs look like "[si: Save]", "[ta: Save]", "[en: Save]").
      if (
        process.env.NODE_ENV !== 'production' &&
        (message.startsWith('[si:') ||
          message.startsWith('[ta:') ||
          message.startsWith('[en:'))
      ) {
        console.warn(
          `[i18n] Untranslated stub: "${namespace}.${key}" = "${message}"`,
        );
      }

      // If params are provided OR the message contains ICU placeholders, run through formatter
      if (params !== undefined || message.includes('{')) {
        return formatMessage(message, locale, params);
      }

      return message;
    },
    [namespace, bundle, locale, fallbackBundle],
  );

  return { t, locale, setLocale };
}
